// Effect Managers
import { take, race, call } from 'redux-saga/effects'
import { delay } from 'redux-saga'

// Utils
import traverse from 'fc-utils/traverse'
import _isEqual from 'lodash/isEqual'

// Local
const getPayloadParams = traverse(['payload', 'queryParams'])
const getPayloadId = traverse(['payload', 'id'])
const getMetaRequestId = traverse(['meta', 'requestId'])

function composeWhenAction ({type: actionType}) {
  function *whenAction ({ id: maybeId, queryParams, requestId, model = {} }) {
    const id = maybeId || model.id || null

    while (true) {
      const action = yield take(actionType)

      if (
        (requestId && requestId === getMetaRequestId(action)) ||
        (queryParams && _isEqual(queryParams, getPayloadParams(action))) ||
        (id && _isEqual(id, getPayloadId(action))) ||
        (!queryParams && !id)
      ) {
        return action
      }
    }
  }

  return function *watchWhenAction ({ timeout = 60000, id, queryParams, requestId } = {}) {
    if (timeout) {
      const { action } = yield race({
        action: call(whenAction, { id, queryParams, requestId }),
        cancelled: call(delay, timeout),
      })

      if (action) {
        return action
      }

      return yield call([Promise, Promise.reject], new Error(`Timed out waiting for ${actionType}`))
    }

    return yield call(whenAction, { id, queryParams, requestId })
  }
}

function *composeNotPermitted ({state, type}) {
  return function *notPermitted () {
    return yield call(
      [Promise, Promise.reject],
      new Error(
        `Controller for ${state.get().model} does not allow ${type}.`
      )
    )
  }
}

// Exports
export default function composeWhen ({
  state,
  actionTypes,
}) {
  const permissions = state.permissions()

  return {
    received: composeWhenAction({type: actionTypes.receive()}),

    fetched: permissions.create
      ? composeWhenAction({type: actionTypes.fetched()})
      : composeNotPermitted({type: 'reading', state}),

    created: permissions.create
      ? composeWhenAction({type: actionTypes.created()})
      : composeNotPermitted({type: 'creation', state}),

    updated: permissions.update
      ? composeWhenAction({type: actionTypes.updated()})
      : composeNotPermitted({type: 'updates', state}),

    deleted: permissions.delete
      ? composeWhenAction({type: actionTypes.deleted()})
      : composeNotPermitted({type: 'deletion', state}),
  }
}
